import * as i0 from '@angular/core';
import { Component, Optional, Self, HostBinding, Input, NgModule } from '@angular/core';
import { NgClass, KeyValuePipe, CommonModule } from '@angular/common';
import * as i2 from '@roosevelt/common-ui-lib/core';
import { coerceBooleanProperty, getUId } from '@roosevelt/common-ui-lib/core';
import * as i1 from '@angular/forms';

/**
 * Checkbox Component
 *
 * @description A checkbox component that comes in a few styles and few colors
 * @example
 * <!-- rs-doc {link: 'Example of basic checkbox with an indeterminate option'} -->
 * <rs-checkbox id="1" name="a" label="option 1" value="a" validation="eager"></rs-checkbox>
 * <rs-checkbox id="2" name="a" label="option 2" value="b"></rs-checkbox>
 * <rs-checkbox id="3" name="a" label="option 3" indeterminate value="b"></rs-checkbox>
 * @example
 * <!-- rs-doc {link: 'Example of checkboxes with disabled and secondary'} -->
 * <rs-checkbox color="secondary" id="5" name="c" label="enabled 1" value="a"></rs-checkbox>
 * <rs-checkbox color="secondary" disabled id="6" name="c" label="disabled 1" checked value="b"></rs-checkbox>
 * <rs-checkbox color="secondary" id="7" name="c" label="enabled 2" checked value="b"></rs-checkbox>
 * <rs-checkbox color="secondary" disabled id="6" name="c" label="disabled 2" value="b"></rs-checkbox>
 * <rs-checkbox color="secondary" [errors]="{required: true}" id="8" name="c" label="value" value="d"></rs-checkbox>
 * @example
 * <!-- rs-doc {link: 'Example of grouped horizontally'} -->
 * <p>Please select an option</p>
 * <div><rs-checkbox id="q3" name="b" label="item 1" value="a"></rs-checkbox></div>
 * <div><rs-checkbox id="q4" name="b" label="item 2" value="b"></rs-checkbox></div>
 * @example
 * <!-- rs-doc {link: 'Example of template driven errors (please note, with errors, the application is responsible for correct messaging and when to turn errors on and off)'} -->
 * <p>Please select an option</p>
 * <div><rs-checkbox [errors]="{required:true}" id="qz3" name="b" label="item 1" value="a"></rs-checkbox></div>
 * <div><rs-checkbox disabled [errors]="{required:true}" id="qz3" name="b" label="item 1" value="a"></rs-checkbox></div>
 * <div><rs-checkbox id="qz4" name="b" label="item 2" value="b"></rs-checkbox></div>
 */
class CheckboxComponent {
    /** if you so choose you can remove the error message from the view and only show the outline */
    get hideErrorMessage() {
        return this._hideErrorMessage;
    }
    set hideErrorMessage(value) {
        this._hideErrorMessage = coerceBooleanProperty(value);
    }
    /** applies tabindex to underlying input */
    get tabindex() {
        return this._tabindex;
    }
    set tabindex(value) {
        this._tabindex = value;
        this.tabBinding = '__reset__';
    }
    /** template driven way to show indeterminate status */
    get indeterminate() {
        return this._indeterminate;
    }
    set indeterminate(value) {
        this._indeterminate = coerceBooleanProperty(value);
    }
    /** template driven way to show a check status */
    get checked() {
        return this._checked;
    }
    set checked(checked) {
        this._checked = coerceBooleanProperty(checked);
        this.onChange(checked);
    }
    /** id applied to underlying control and label */
    get id() {
        return this._id;
    }
    set id(value) {
        this._id = value;
        this.idBinding = '__reset__';
    }
    /** template driven way to apply disabled */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this.setDisabledState(coerceBooleanProperty(value));
    }
    /** Allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    constructor(_parentForm, _parentFormGroup, ngControl, validationMessages, elementRef) {
        this._parentForm = _parentForm;
        this._parentFormGroup = _parentFormGroup;
        this.ngControl = ngControl;
        this.validationMessages = validationMessages;
        this.elementRef = elementRef;
        this.idBinding = undefined;
        this.tabBinding = undefined;
        /** This is the text to the right of the check mark */
        this.label = '';
        /** whether we should show errors right away or after form is submitted */
        this.validation = 'lazy';
        /** template driven errors following the reactive form style. for example this would show required {required: true}. follow the error message map provider */
        this.errors = null;
        this.parent = undefined;
        this._hideErrorMessage = false;
        this._color = 'primary';
        this._disabled = false;
        this._checked = false;
        this._id = undefined;
        this._tabindex = '';
        this._indeterminate = false;
        this.onChange = () => { };
        this.onTouched = () => { };
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }
    /* Implemented as part of ControlValueAccessor. */
    writeValue(checked) {
        if (checked || checked === false) {
            this.checked = checked;
        }
    }
    /* Implemented as part of ControlValueAccessor. */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /* Implemented as part of ControlValueAccessor. */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
    }
    changed(event) {
        this.checked = event.target.checked;
    }
    touch() {
        this.onTouched();
    }
    getValidationMessage(error) {
        if (this.validationMessages[error]) {
            return this.validationMessages[error]((this.errors || this.ngControl.errors || {})[error]);
        }
        else {
            console.warn(error + ' does not have a corresponding message, defaulting to \'Invalid\'. please update the error message map to reflect. https://tst.my.rooseveltsolutions.com/style-guide/error-messages');
            return 'Invalid';
        }
    }
    ngOnInit() {
        this.color = this.color;
        this.parent = this._parentFormGroup || this._parentForm;
        if (!this.id) {
            /* if (this.label) {
              console.warn('CHECKBOX_VALIDATION_WARNING', 'without setting an id, checkbox ' + (this.ngControl ? this.ngControl.name : this.label) + ' label may not work. please provide an id.');
            } */
            this.id = getUId();
        }
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_disabled = undefined; }
    static { this.ngAcceptInputType_checked = undefined; }
    static { this.ngAcceptInputType_indeterminate = undefined; }
    static { this.ngAcceptInputType_hideErrorMessage = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CheckboxComponent, deps: [{ token: i1.NgForm, optional: true }, { token: i1.FormGroupDirective, optional: true }, { token: i1.NgControl, optional: true, self: true }, { token: i2.ErrorMessageMap }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: CheckboxComponent, isStandalone: true, selector: "rs-checkbox", inputs: { label: "label", validation: "validation", errors: "errors", hideErrorMessage: "hideErrorMessage", tabindex: "tabindex", indeterminate: "indeterminate", checked: "checked", id: "id", disabled: "disabled", color: "color" }, host: { properties: { "attr.id": "this.idBinding", "attr.tabindex": "this.tabBinding" } }, ngImport: i0, template: "<div class=\"checkbox-wrapper\">\n  <label [for]=\"id\" [class.disabled]=\"disabled\">\n    <input [disabled]=\"disabled\" [ngClass]=\"{\n      'invalid': ((ngControl && ngControl.invalid) || (!ngControl && errors)) && ((validation === 'lazy' && (!ngControl || parent.submitted || ngControl.touched)) || validation === 'eager'),\n      'valid': ((ngControl && !ngControl.invalid) || (!ngControl && !errors)) && validation === 'eager'\n  }\" [id]=\"id\" type=\"checkbox\" [checked]=\"checked\" [indeterminate]=\"indeterminate\" (change)=\"changed($event)\"\n      (blur)=\"touch()\" [tabindex]=\"tabindex\">\n      <span class=\"checkmark\"></span>\n      <span class=\"checkmark-focus\"></span>\n      <span>{{label}}</span>\n    </label>\n    @if (!hideErrorMessage && ngControl && ngControl.invalid && ((validation === 'lazy' && (parent.submitted || ngControl.touched)) || validation === 'eager')) {\n      @for (error of ngControl.errors | keyvalue; track $index) {\n        <div class=\"alert alert-danger\" role=\"alert\">{{getValidationMessage(error.key)}}</div>\n      }\n    }\n    @if (!hideErrorMessage && !ngControl && errors) {\n      @for (error of errors | keyvalue; track $index) {\n        <div class=\"alert alert-danger\" role=\"alert\">{{getValidationMessage(error.key)}}</div>\n      }\n    }\n  </div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}.checkbox-wrapper{margin-bottom:12px;margin-right:30px;position:relative;cursor:pointer;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;display:inline-flex;min-height:25px;justify-content:center;flex-direction:column}.checkbox-wrapper label{display:flex;align-items:center}.checkbox-wrapper.disabled{cursor:not-allowed}.checkbox-wrapper input{position:absolute;opacity:0;cursor:pointer;height:0;width:0}.checkbox-wrapper input:focus~.checkmark-focus{position:absolute;height:31px;width:31px;left:-3px;display:inline-block;border-width:2px;border-style:solid;border-radius:3px}.checkbox-wrapper input.invalid+.checkmark{border-color:#a94442}.checkbox-wrapper input.valid+.checkmark{border-color:#42a948}.checkmark{position:relative;display:inline-block;height:25px;width:25px;background-color:transparent;border-style:solid;border-width:2px;border-radius:3px;margin:2px 8px 2px 0}.checkbox-wrapper input:checked~.disabled,.checkbox-wrapper input:indeterminate~.disabled{cursor:not-allowed}.checkmark:after{content:\"\";position:absolute;display:none}.checkbox-wrapper input:checked~.checkmark:after{display:block;background:url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjRweCIgZmlsbD0iI0ZGRiI+PHBhdGggZD0iTTkgMTYuMTdMNC44MyAxMmwtMS40MiAxLjQxTDkgMTkgMjEgN2wtMS40MS0xLjQxTDkgMTYuMTd6Ii8+PC9zdmc+) 0 0 no-repeat}.checkbox-wrapper input:indeterminate~.checkmark:after{display:block;background:url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjRweCIgZmlsbD0iI0ZGRiI+PHBhdGggZD0iTTE5IDEzSDV2LTJoMTR2MnoiLz48L3N2Zz4=) 0 0 no-repeat}.checkbox-wrapper .checkmark:after{top:-2px;left:-1px;display:inline-block;width:24px;height:24px;content:\"\";background-size:100%;position:absolute}.alert-danger{color:#a94442;font-weight:400;font-size:.75em}.alert-danger:before{content:\"     *   \";position:relative;top:3px}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: KeyValuePipe, name: "keyvalue" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-checkbox', standalone: true, imports: [NgClass, KeyValuePipe], template: "<div class=\"checkbox-wrapper\">\n  <label [for]=\"id\" [class.disabled]=\"disabled\">\n    <input [disabled]=\"disabled\" [ngClass]=\"{\n      'invalid': ((ngControl && ngControl.invalid) || (!ngControl && errors)) && ((validation === 'lazy' && (!ngControl || parent.submitted || ngControl.touched)) || validation === 'eager'),\n      'valid': ((ngControl && !ngControl.invalid) || (!ngControl && !errors)) && validation === 'eager'\n  }\" [id]=\"id\" type=\"checkbox\" [checked]=\"checked\" [indeterminate]=\"indeterminate\" (change)=\"changed($event)\"\n      (blur)=\"touch()\" [tabindex]=\"tabindex\">\n      <span class=\"checkmark\"></span>\n      <span class=\"checkmark-focus\"></span>\n      <span>{{label}}</span>\n    </label>\n    @if (!hideErrorMessage && ngControl && ngControl.invalid && ((validation === 'lazy' && (parent.submitted || ngControl.touched)) || validation === 'eager')) {\n      @for (error of ngControl.errors | keyvalue; track $index) {\n        <div class=\"alert alert-danger\" role=\"alert\">{{getValidationMessage(error.key)}}</div>\n      }\n    }\n    @if (!hideErrorMessage && !ngControl && errors) {\n      @for (error of errors | keyvalue; track $index) {\n        <div class=\"alert alert-danger\" role=\"alert\">{{getValidationMessage(error.key)}}</div>\n      }\n    }\n  </div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}.checkbox-wrapper{margin-bottom:12px;margin-right:30px;position:relative;cursor:pointer;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;display:inline-flex;min-height:25px;justify-content:center;flex-direction:column}.checkbox-wrapper label{display:flex;align-items:center}.checkbox-wrapper.disabled{cursor:not-allowed}.checkbox-wrapper input{position:absolute;opacity:0;cursor:pointer;height:0;width:0}.checkbox-wrapper input:focus~.checkmark-focus{position:absolute;height:31px;width:31px;left:-3px;display:inline-block;border-width:2px;border-style:solid;border-radius:3px}.checkbox-wrapper input.invalid+.checkmark{border-color:#a94442}.checkbox-wrapper input.valid+.checkmark{border-color:#42a948}.checkmark{position:relative;display:inline-block;height:25px;width:25px;background-color:transparent;border-style:solid;border-width:2px;border-radius:3px;margin:2px 8px 2px 0}.checkbox-wrapper input:checked~.disabled,.checkbox-wrapper input:indeterminate~.disabled{cursor:not-allowed}.checkmark:after{content:\"\";position:absolute;display:none}.checkbox-wrapper input:checked~.checkmark:after{display:block;background:url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjRweCIgZmlsbD0iI0ZGRiI+PHBhdGggZD0iTTkgMTYuMTdMNC44MyAxMmwtMS40MiAxLjQxTDkgMTkgMjEgN2wtMS40MS0xLjQxTDkgMTYuMTd6Ii8+PC9zdmc+) 0 0 no-repeat}.checkbox-wrapper input:indeterminate~.checkmark:after{display:block;background:url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAwIDI0IDI0IiB3aWR0aD0iMjRweCIgZmlsbD0iI0ZGRiI+PHBhdGggZD0iTTE5IDEzSDV2LTJoMTR2MnoiLz48L3N2Zz4=) 0 0 no-repeat}.checkbox-wrapper .checkmark:after{top:-2px;left:-1px;display:inline-block;width:24px;height:24px;content:\"\";background-size:100%;position:absolute}.alert-danger{color:#a94442;font-weight:400;font-size:.75em}.alert-danger:before{content:\"     *   \";position:relative;top:3px}\n"] }]
        }], ctorParameters: () => [{ type: i1.NgForm, decorators: [{
                    type: Optional
                }] }, { type: i1.FormGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: i1.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }] }, { type: i2.ErrorMessageMap }, { type: i0.ElementRef }], propDecorators: { idBinding: [{
                type: HostBinding,
                args: ['attr.id']
            }], tabBinding: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], label: [{
                type: Input
            }], validation: [{
                type: Input
            }], errors: [{
                type: Input
            }], hideErrorMessage: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], indeterminate: [{
                type: Input
            }], checked: [{
                type: Input
            }], id: [{
                type: Input
            }], disabled: [{
                type: Input
            }], color: [{
                type: Input
            }] } });

class CheckboxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: CheckboxModule, imports: [CommonModule,
            CheckboxComponent], exports: [CheckboxComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CheckboxModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CheckboxComponent
                    ],
                    exports: [
                        CheckboxComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CheckboxComponent, CheckboxModule };
